import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useResizeObserver } from '@wojtekmaj/react-hooks';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';


// Set the workerSrc to the local path of the worker file
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/js/pdf.worker.min.mjs`;

const Pagination = ({ pageNumber, numPages, changePage, inputPageNumber, handlePageInputChange }) => {
  return (
    <nav aria-label="Page navigation">
      <ul className="pagination justify-content-center">
        <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => changePage(1)}
            disabled={pageNumber === 1}
          >
            &laquo;&laquo;
          </button>
        </li>
        <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => changePage(pageNumber - 1)}
            disabled={pageNumber === 1}
          >
            &laquo;
          </button>
        </li>
        <li className="page-item">
          <input
            type="number"
            className="form-control"
            value={inputPageNumber}
            onChange={handlePageInputChange}
            style={{ width: '80px', textAlign: 'center' }}
            min={1}
            max={numPages}
          />
        </li>
        <li className={`page-item ${pageNumber === numPages ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => changePage(pageNumber + 1)}
            disabled={pageNumber === numPages}
          >
            &raquo;
          </button>
        </li>
        <li className={`page-item ${pageNumber === numPages ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => changePage(numPages)}
            disabled={pageNumber === numPages}
          >
            &raquo;&raquo;
          </button>
        </li>
      </ul>
    </nav>
  );
};

// A region's `points` are the corners of one source element's bounding box, in
// the pixel coordinate space unstructured measured it in (region.system.width/
// height - see create_embeddings.py's _summarize_orig_elements). Reducing to a
// min/max rect handles both an already-axis-aligned box and any near-rectangular
// polygon unstructured might report.
const regionToRect = (region) => {
  const xs = region.points.map(([x]) => x);
  const ys = region.points.map(([, y]) => y);
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
};

const PdfViewer = ({ pdf, initialPage = 1, isOpen, onClose, onLoadError, regions = [] }) => {
  // Computed once per region and reused below for both the best-page area sum
  // and the overlay layout, instead of calling regionToRect twice per region.
  const regionRects = useMemo(
    () => regions.map((region) => ({ region, rect: regionToRect(region) })),
    [regions]
  );

  // A chunk occasionally spans two PDF pages (chunk_by_title guarantees a
  // shared section, not a shared page), and citation.page - the chunk's FIRST
  // page - can then land the viewer on a page holding only a stray trailing
  // element (e.g. a page-number footer misclassified as a Title), while the
  // actual cited text sits one page later. Weighting each candidate page by
  // its regions' total area (rather than trusting citation.page) finds the
  // page carrying the real passage instead of a footer scrap.
  const bestRegionPage = useMemo(() => {
    if (!regionRects.length) return null;
    const areaByPage = new Map();
    for (const { region, rect } of regionRects) {
      const page = (region.page ?? 0) + 1;
      areaByPage.set(page, (areaByPage.get(page) || 0) + rect.width * rect.height);
    }
    let bestPage = null;
    let bestArea = -1;
    for (const [page, area] of areaByPage) {
      if (area > bestArea) {
        bestPage = page;
        bestArea = area;
      }
    }
    return bestPage;
  }, [regionRects]);

  // Seeded here (rather than from the raw `initialPage` prop) so the very
  // first render already targets the region-corrected page instead of
  // flashing citation.page's page first and correcting a render later.
  const effectiveInitialPage = bestRegionPage ?? initialPage;

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(effectiveInitialPage);
  const [inputPageNumber, setInputPageNumber] = useState(effectiveInitialPage);
  const [containerRef, setContainerRef] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const firstHighlightRef = useRef(null);

  const maxWidth = 800;
  const renderedWidth = containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth;

  // regions carry every source element behind the cited chunk, each tagged with
  // its own (0-indexed) page - only the ones on the page currently on screen
  // are drawn, so paging through a multi-page chunk highlights as you go.
  const pageRegions = useMemo(
    () => regionRects.filter(({ region }) => (region.page ?? 0) + 1 === pageNumber),
    [regionRects, pageNumber]
  );

  // withCredentials so pdf.js's own range-request fetches carry the session
  // cookie cross-origin (documents/views.py's ViewPDF requires login) - a
  // bare URL string wouldn't include credentials on a cross-origin request.
  const file = useMemo(() => (pdf ? { url: pdf, withCredentials: true } : null), [pdf]);

  const onResize = useCallback((entries) => {
    const [entry] = entries;
    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  useResizeObserver(containerRef, {}, onResize);

  useEffect(() => {
    setPageNumber(effectiveInitialPage);
    setInputPageNumber(effectiveInitialPage);
  }, [pdf, effectiveInitialPage]);

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Scroll the cited passage into view rather than leaving the reader to find
  // it on a full page render - runs after the page (and the overlay sized to
  // it) has painted, so the highlight's real position is already on screen.
  useEffect(() => {
    if (!pageRegions.length) return;
    const frame = requestAnimationFrame(() => {
      firstHighlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [pageRegions]);

  const changePage = (page) => {
    if (page >= 1 && page <= numPages) {
      setPageNumber(page);
      setInputPageNumber(page);
    }
  };

  const handlePageInputChange = (event) => {
    const pageNumber = parseInt(event.target.value, 10);
    if (!isNaN(pageNumber)) {
      setInputPageNumber(pageNumber);
      changePage(pageNumber);
    }
  };

  return (
    <>
      <div className={`pdf-viewer-container ${isOpen ? 'open' : ''}`} ref={setContainerRef}>
        <div className="pdf-viewer-content">
          {/* Top Pagination */}
          <Pagination
            pageNumber={pageNumber}
            numPages={numPages}
            changePage={changePage}
            inputPageNumber={inputPageNumber}
            handlePageInputChange={handlePageInputChange}
          />

          {/* PDF Document */}
          <Document
            file={file}
            onLoadSuccess={handleDocumentLoadSuccess}
            onLoadError={onLoadError}
            loading={<div className="pdf-viewer-loading text-center text-muted py-5">Loading document…</div>}
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Page pageNumber={pageNumber || 1} width={renderedWidth} />
              {pageRegions.map(({ region, rect }, index) => {
                const scale = renderedWidth / (region.system?.width || renderedWidth);
                return (
                  <div
                    key={index}
                    ref={index === 0 ? firstHighlightRef : null}
                    style={{
                      position: 'absolute',
                      left: rect.left * scale,
                      top: rect.top * scale,
                      width: rect.width * scale,
                      height: rect.height * scale,
                      background: 'rgba(255, 214, 0, 0.35)',
                      border: '1.5px solid rgba(224, 168, 0, 0.9)',
                      borderRadius: 2,
                      pointerEvents: 'none',
                    }}
                  />
                );
              })}
            </div>
          </Document>

          {/* Bottom Pagination */}
          <Pagination
            pageNumber={pageNumber}
            numPages={numPages}
            changePage={changePage}
            inputPageNumber={inputPageNumber}
            handlePageInputChange={handlePageInputChange}
          />
        </div>
      </div>
      <div className={`overlay ${isOpen ? 'blurred' : ''}`} onClick={onClose}></div>
    </>
  );
};

export default PdfViewer;
